import { useReducer, useEffect } from 'react';
import useApiClient from '../../modules/react/hooks/useApiClient';

const initialState = {
  fulltext: undefined,
  error: undefined,
  loading: false,
  pages: undefined,
  queryCount: 0,
  totalCount: 0,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'edges':
      return {
        ...state,
        pages: action.edges.map(edge => edge.node),
        queryCount: action.queryCount,
      };
    case 'error':
      return {
        ...state,
        error: action.error,
      };
    case 'fulltext':
      return {
        ...state,
        fulltext: action.fulltext,
      };
    case 'loading':
      return {
        ...state,
        error: null,
        loading: true,
      };
    case 'loaded':
      return {
        ...state,
        loading: false,
      };
    case 'reset':
      return {
        ...initialState,
      };
    case 'totalCount':
      return {
        ...state,
        totalCount: action.totalCount,
      };
    default:
      return initialState;
  }
};

const useSearchablePages = (query, options, dependencies = [], { shouldQuery = true } = {}) => {
  const apiClient = useApiClient();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { fulltext } = state;

  const queryOptions = typeof options === 'function'
    ? options(state)
    : options;

  const fetch = () => {
    apiClient.query({ query, ...queryOptions }).then((res) => {
      dispatch({ type: 'edges', edges: res.data.pages.edges, queryCount: res.data.pages.totalCount });

      // We only care about the total number of pages available for a query if
      // it's a query for _all_ pages, not when it's a query for a subset of pages
      // pared down by a search.
      if (!fulltext) {
        dispatch({ type: 'totalCount', totalCount: res.data.pages.totalCount });
      }
    }).catch((err) => {
      dispatch({ type: 'error', error: err });
    }).finally(() => {
      dispatch({ type: 'loaded' });
    });
  };

  const onChange = (value) => {
    dispatch({ type: 'fulltext', fulltext: value });
  };

  const onReset = () => {
    dispatch({ type: 'reset' });
  };

  useEffect(() => {
    if (!shouldQuery) {
      return;
    }

    dispatch({ type: 'loading' });
    fetch();
  }, [
    fulltext,
    ...dependencies,
  ]);

  return [state, {
    onChange,
    onReset,
  }];
};

export default useSearchablePages;
